import { BlockId } from '../world/BlockType.js';
import { EYE_HEIGHT, DAMAGE_COOLDOWN } from '../constants.js';

// Health & hunger constants
const MAX_HEALTH = 20;
const MAX_HUNGER = 20;
const INITIAL_SATURATION = 5;
const SPRINT_HUNGER_THRESHOLD = 6;

// Fall damage
const FALL_VELOCITY_THRESHOLD = -2;
const SAFE_FALL_DISTANCE = 3;

// Cactus
const CACTUS_DAMAGE = 1;

// Drowning
const MAX_AIR_SUPPLY = 300;         // 15 seconds at 20/s drain
const AIR_DRAIN_RATE = 20;
const AIR_RECOVERY_RATE = 150;
const DROWN_DAMAGE_INTERVAL = 1;
const DROWN_DAMAGE = 2;

// Hunger
const HUNGER_DRAIN_INTERVAL = 60;   // seconds per passive hunger point
const STARVATION_DAMAGE_INTERVAL = 4;
const STARVATION_DAMAGE = 1;

// Regen
const REGEN_HUNGER_THRESHOLD = 18;
const REGEN_INTERVAL = 0.5;

// Exhaustion
const EXHAUSTION_THRESHOLD = 4;
const EXHAUSTION_JUMP = 0.05;
const EXHAUSTION_SPRINT_RATE = 0.1;
const EXHAUSTION_MINE = 0.005;
const EXHAUSTION_ATTACK = 0.1;
const EXHAUSTION_SWIM_RATE = 0.01;
const EXHAUSTION_TAKE_DAMAGE = 0.1;

// Visual feedback
const DAMAGE_FLASH_DURATION = 0.3;

/**
 * Health, Hunger & Saturation system - Minecraft style
 */
export class Health {
  constructor() {
    this.maxHealth = MAX_HEALTH;
    this.health = MAX_HEALTH;
    this.maxHunger = MAX_HUNGER;
    this.hunger = MAX_HUNGER;
    this.dead = false;
    this.lastDamageSource = null; // Track cause of death

    // Saturation system
    this.saturation = INITIAL_SATURATION;
    this.exhaustion = 0;

    // Sprint tracking
    this.sprinting = false;

    // Damage cooldown (invincibility frames)
    this.damageCooldown = 0;

    // Fall damage tracking
    this._fallStartY = null;
    this._wasFalling = false;

    // Hunger drain timer
    this._hungerTimer = 0;
    this._starvationTimer = 0;

    // Regen timer
    this._regenTimer = 0;

    // Death callback
    this.onDeath = null;

    // Fall damage callback (amount) => void
    this.onFallDamage = null;

    // Damage flash timer (for red screen flash)
    this.damageFlash = 0;

    // Drowning system
    this.maxAirSupply = MAX_AIR_SUPPLY;
    this.airSupply = MAX_AIR_SUPPLY;
    this._drownTimer = 0;

    // Creative mode: invincible
    this.creativeMode = false;

    // Difficulty config (set by main.js)
    this.difficultyConfig = null;

    // Shield blocking
    this.shieldActive = false;
    this.shieldCooldown = 0;
  }

  get canSprint() {
    return this.hunger > SPRINT_HUNGER_THRESHOLD;
  }

  update(dt, player, world) {
    if (this.dead) return;

    // Update cooldowns
    if (this.damageCooldown > 0) this.damageCooldown -= dt;
    if (this.damageFlash > 0) this.damageFlash -= dt;
    if (this.shieldCooldown > 0) this.shieldCooldown -= dt;

    // Creative mode: no damage, no hunger, no drowning
    if (this.creativeMode) {
      this.airSupply = this.maxAirSupply;
      return;
    }

    // --- Fall damage ---
    const isFalling = player.velocity.y < FALL_VELOCITY_THRESHOLD && !player.onGround && !player.flying && !player.inWater;
    if (isFalling && !this._wasFalling) {
      this._fallStartY = player.position.y;
    }
    if (this._wasFalling && player.onGround && this._fallStartY !== null) {
      const fallDist = this._fallStartY - player.position.y;
      if (fallDist > SAFE_FALL_DISTANCE) {
        const damage = Math.floor(fallDist - SAFE_FALL_DISTANCE);
        this.takeDamage(damage, 'Ca\u00edda fatal');
        if (damage >= 1 && this.onFallDamage) {
          this.onFallDamage(damage);
        }
      }
      this._fallStartY = null;
    }
    this._wasFalling = isFalling;

    // --- Cactus damage ---
    if (world) {
      const px = Math.floor(player.position.x);
      const py = Math.floor(player.position.y);
      const pz = Math.floor(player.position.z);
      const feetBlock = world.getBlock(px, py, pz);
      const bodyBlock = world.getBlock(px, py + 1, pz);
      if (feetBlock === BlockId.CACTUS || bodyBlock === BlockId.CACTUS) {
        this.takeDamage(CACTUS_DAMAGE, 'Pinchado por cactus');
      }
    }

    // --- Drowning ---
    if (world) {
      const headY = Math.floor(player.position.y + EYE_HEIGHT);
      const headX = Math.floor(player.position.x);
      const headZ = Math.floor(player.position.z);
      const headBlock = world.getBlock(headX, headY, headZ);
      if (headBlock === BlockId.WATER) {
        this.airSupply -= dt * AIR_DRAIN_RATE;
        if (this.airSupply <= 0) {
          this.airSupply = 0;
          this._drownTimer += dt;
          if (this._drownTimer >= DROWN_DAMAGE_INTERVAL) {
            this._drownTimer = 0;
            this.takeDamage(DROWN_DAMAGE, 'Ahogado');
          }
        }
      } else {
        this.airSupply = Math.min(this.maxAirSupply, this.airSupply + dt * AIR_RECOVERY_RATE);
        this._drownTimer = 0;
      }
    }

    // --- Process exhaustion into saturation/hunger drain ---
    this._processExhaustion();

    // --- Base hunger drain (passive timer) ---
    this._hungerTimer += dt;
    if (this._hungerTimer >= HUNGER_DRAIN_INTERVAL) {
      this._hungerTimer = 0;
      if (this.saturation > 0) {
        this.saturation = Math.max(0, this.saturation - 1);
      } else {
        this.hunger = Math.max(0, this.hunger - 1);
      }
    }

    // --- Auto-disable sprint when hunger too low ---
    if (this.sprinting && !this.canSprint) {
      this.sprinting = false;
    }

    // --- Starvation damage ---
    if (this.hunger <= 0) {
      this._starvationTimer += dt;
      if (this._starvationTimer >= STARVATION_DAMAGE_INTERVAL) {
        this._starvationTimer = 0;
        this.takeDamage(STARVATION_DAMAGE, 'Inanici\u00f3n');
      }
    } else {
      this._starvationTimer = 0;
    }

    // --- Health regeneration ---
    const passiveRegen = this.difficultyConfig && this.difficultyConfig.passiveRegen;
    if (passiveRegen && this.health < this.maxHealth) {
      // Peaceful: always regen, faster (every 0.5s)
      this._regenTimer += dt;
      if (this._regenTimer >= 0.5) {
        this._regenTimer = 0;
        this.health = Math.min(this.maxHealth, this.health + 1);
      }
    } else if (this.hunger >= REGEN_HUNGER_THRESHOLD && this.health < this.maxHealth) {
      this._regenTimer += dt;
      if (this._regenTimer >= REGEN_INTERVAL) {
        this._regenTimer = 0;
        if (this.saturation > 0) {
          this.saturation = Math.max(0, this.saturation - 1);
        }
        this.health = Math.min(this.maxHealth, this.health + 1);
      }
    } else {
      this._regenTimer = 0;
    }

    // --- Clamp saturation to current hunger ---
    this.saturation = Math.min(this.saturation, this.hunger);
  }

  _processExhaustion() {
    while (this.exhaustion >= EXHAUSTION_THRESHOLD) {
      this.exhaustion -= EXHAUSTION_THRESHOLD;
      if (this.saturation > 0) {
        this.saturation = Math.max(0, this.saturation - 1);
      } else {
        this.hunger = Math.max(0, this.hunger - 1);
      }
    }
  }

  onJump() {
    if (this.dead) return;
    this.exhaustion += EXHAUSTION_JUMP;
  }

  onSprint(dt) {
    if (this.dead) return;
    this.exhaustion += EXHAUSTION_SPRINT_RATE * dt;
  }

  onMine() {
    if (this.dead) return;
    this.exhaustion += EXHAUSTION_MINE;
  }

  onAttack() {
    if (this.dead) return;
    this.exhaustion += EXHAUSTION_ATTACK;
  }

  onSwim(dt) {
    if (this.dead) return;
    this.exhaustion += EXHAUSTION_SWIM_RATE * dt;
  }

  onTakeDamage() {
    if (this.dead) return;
    this.exhaustion += EXHAUSTION_TAKE_DAMAGE;
  }

  takeDamage(amount, source) {
    if (this.dead || this.creativeMode || this._hiddenInGrave || this._magicShield || this.damageCooldown > 0 || !amount || amount < 1) return;

    // Shield blocking: absorb 100% damage
    if (this.shieldActive && this.shieldCooldown <= 0) {
      this.shieldCooldown = 5; // 5 second cooldown
      this.damageCooldown = DAMAGE_COOLDOWN;
      if (this.onShieldBlock) this.onShieldBlock();
      return;
    }

    if (source) this.lastDamageSource = source;
    amount = Math.max(1, Math.floor(amount));
    this.health = Math.max(0, this.health - amount);
    this.damageCooldown = DAMAGE_COOLDOWN;
    this.damageFlash = DAMAGE_FLASH_DURATION;
    this.onTakeDamage();
    if (this.onDamageNumber) this.onDamageNumber(amount);
    if (this.health <= 0) {
      this.dead = true;
      if (this.onDeath) this.onDeath();
    }
  }

  heal(amount) {
    if (this.dead) return;
    this.health = Math.min(this.maxHealth, this.health + amount);
  }

  feed(hungerAmount, saturationAmount = 0) {
    this.hunger = Math.min(this.maxHunger, this.hunger + hungerAmount);
    this.saturation = Math.min(this.hunger, this.saturation + saturationAmount);
  }

  respawn() {
    this.health = this.maxHealth;
    this.hunger = this.maxHunger;
    this.saturation = INITIAL_SATURATION;
    this.exhaustion = 0;
    this.sprinting = false;
    this.dead = false;
    this.lastDamageSource = null;
    this.damageCooldown = 0;
    this.damageFlash = 0;
    this._fallStartY = null;
    this._wasFalling = false;
    this._hungerTimer = 0;
    this._starvationTimer = 0;
    this._regenTimer = 0;
    this.airSupply = this.maxAirSupply;
    this._drownTimer = 0;
    this.shieldActive = false;
    this.shieldCooldown = 0;
  }

  drainHunger(amount) {
    if (amount <= 0) return;
    if (this.saturation > 0) {
      const satDrain = Math.min(this.saturation, amount);
      this.saturation -= satDrain;
      amount -= satDrain;
    }
    if (amount > 0) {
      this.hunger = Math.max(0, this.hunger - amount);
    }
  }

  getAirPercent() {
    return this.airSupply / this.maxAirSupply;
  }

  isUnderwater() {
    return this.airSupply < this.maxAirSupply;
  }
}
